"use client";

import { useState, useEffect, useRef } from "react";
import Icon from "@/components/ui/AppIcon";

const faqs = [
  {
    q: "How much does a DogFob partner account cost?",
    a: "The basic partner profile is completely free — you can list your business, appear on the partner map, and receive pet profiles from consenting clients at no cost. Premium features (automated reminders, loyalty tools, analytics dashboard, and priority support) are available from $29/month. No long-term contracts.",
  },
  {
    q: "Do I need my clients to already be on DogFob?",
    a: "No. When you join as a partner, you can invite existing clients by email or share a QR code. They create a free pet profile and connect it to your business with one tap. We also provide printable sign-up cards you can leave at your reception desk.",
  },
  {
    q: "Can I see a pet\'s full medical history?",
    a: "You can only see what the owner explicitly shares with you. Owners choose which sections of their pet\'s profile are visible to each professional. Most owners share relevant health records with their vet and grooming notes with their groomer. You\'ll always know exactly what level of access you have.",
  },
  {
    q: "How does the automated reminder system work?",
    a: "You configure reminder rules in your partner dashboard — for example, 'send a reminder 7 days before a vaccine is due' or 'send a rebook nudge 6 weeks after a grooming appointment.' DogFob handles the delivery via push notification and email. You can customize the message tone and frequency.",
  },
  {
    q: "Is DogFob compliant with veterinary data regulations?",
    a: "Yes. DogFob is designed with privacy-first architecture. All data is encrypted in transit and at rest. Our consent model is fully opt-in — no data is shared without explicit owner permission. We\'re SOC 2 compliant and we\'re actively working toward HIPAA-adjacent standards for veterinary use cases.",
  },
  {
    q: "What if I want to cancel my partner account?",
    a: "You can cancel at any time with no penalty. Your business listing will be removed from the partner map, and you'll lose access to premium tools. Client connections and their pet profiles remain under the owners' control — you'll simply lose the ability to view them.",
  },
];

export default function BusinessFAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target
              .querySelectorAll(".scroll-fade-up")
              .forEach((el, i) => {
                setTimeout(() => el.classList.add("is-visible"), i * 80);
              });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-24 dark:bg-[#0f172a]"
      style={{ background: "linear-gradient(160deg, #F0FAFB 0%, #F7F8F6 100%)" }}
    >
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-14 scroll-fade-up">
          <span className="teal-badge mb-4">FAQ</span>
          <h2 className="text-4xl font-extrabold text-[#1A2B2B] dark:text-slate-100 tracking-tight mt-4 mb-4">
            Partner questions, answered.
          </h2>
          <p className="text-[#4A6363] dark:text-slate-400 text-lg">
            Everything pet businesses ask before joining DogFob.
          </p>
        </div>

        <div className="flex flex-col gap-0">
          {faqs?.map((faq, idx) => (
            <div key={`biz-faq-${idx}`} className="scroll-fade-up faq-item">
              <button
                className="w-full flex items-center justify-between py-5 text-left gap-4 hover:text-teal-600 transition-colors duration-200"
                onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                aria-expanded={openIndex === idx}
              >
                <span className="text-base font-semibold text-[#1A2B2B] dark:text-slate-100 font-heading pr-2">
                  {faq?.q}
                </span>
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                    openIndex === idx ? "bg-teal-500 rotate-45" : "bg-white dark:bg-slate-700 border border-[#E2EDED] dark:border-slate-600"
                  }`}
                >
                  <Icon
                    name="PlusIcon"
                    size={14}
                    className={openIndex === idx ? "text-white" : "text-[#4A6363]"}
                  />
                </div>
              </button>
              <div
                className="faq-answer"
                style={{ maxHeight: openIndex === idx ? "300px" : "0" }}
              >
                <p className="pb-5 text-[#4A6363] dark:text-slate-400 leading-relaxed text-sm">{faq?.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}